import Select from "../../components/ui/Select";
import Button from "../../components/ui/Button";
import Pagination from "../../components/ui/Pagination";
import CustomerShipmentsList from "./CustomerShipmentsList";
import { formatMoney } from "../../utils/formatMoney";
import Can from "../../compoonents/Can";
import { useAccess } from "../../hooks/useAccess";
import React, { useState, useEffect, useCallback } from "react";
import "./ShipmentPage.css";
import { getShipmentSettings, createShipmentSettings, updateShipmentRate, } from "../../services/shipmentService";
import { toast } from "react-toastify";
import { ViewMode, ShipmentSetting, ShipmentSettingState, FormData, } from "../../types/shipment";
import { IoSearchOutline, IoPencil, IoTrashOutline, } from "react-icons/io5";
import noShipmentIcon from "../../assets/images/Group 1000003472.png";
import ComingSoonAction from "../../compoonents/ComingSoonAction/ComingSoonAction";
type ShipmentTab = "shipment-pricing" | "customer-shipments";
const ShipmentPage: React.FC = () => {
    const { can } = useAccess();
    const [activeTab, setActiveTab] = useState<ShipmentTab>(can("shipping_rates.read") ? "shipment-pricing" : "customer-shipments");
    const [headerSearch, setHeaderSearch] = useState("");
    return (<div className="shipment-page">

      <div className="page-content-wrapper">
        <header className="content-header">
          <div className="shipment-tabs">
            <Can permission="shipping_rates.read"><button className={activeTab === "shipment-pricing" ? "active" : ""} onClick={() => setActiveTab("shipment-pricing")}>
              Shipment Settings
            </button></Can>
            <Can permission="shipments.read"><button className={activeTab === "customer-shipments" ? "active" : ""} onClick={() => setActiveTab("customer-shipments")}>
              Customer Shipments
            </button></Can>
          </div>
          <div className="page-controls">
            <div className="input-with-icon search-bar">
              <IoSearchOutline />
              <input type="text" placeholder="Search..." value={headerSearch} onChange={(e) => setHeaderSearch(e.target.value)}/>
            </div>
          </div>
        </header>
        <div className="shipment-content">
          {activeTab === "shipment-pricing" && can("shipping_rates.read") && (<ShipmentPricingTab searchTerm={headerSearch}/>)}
          {activeTab === "customer-shipments" && can("shipments.read") && (<CustomerShipmentsList searchTerm={headerSearch}/>)}
        </div>
      </div>
    </div>);
};
const matchesSearch = (text: string, term: string) => !term || text.toLowerCase().includes(term.toLowerCase());
const ShipmentPricingTab: React.FC<{
    searchTerm: string;
}> = ({ searchTerm }) => {
    const [editingItem, setEditingItem] = useState<ShipmentSettingState | null>(null);
    const [settings, setSettings] = useState<ShipmentSetting[]>([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [viewMode, setViewMode] = useState<ViewMode>("table");
    const [editMode, setEditMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        fromCountry: "",
        toCountry: "",
        pricePerKg: 0,
        clearanceFee: 0,
        deliveryDays: "0",
        currency: "NGN",
        category: "",
        goodType: "",
        shipmentType: "",
        deliveryType: "",
        weight: 1,
    });
    const fetchSettings = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await getShipmentSettings(page, 20);
            setSettings(response.data.settings || []);
            setPages(Math.max(1, response.data.pagination.totalPages));
            setTotal(response.data.pagination.totalItems);
        }
        catch (error) {
            setError("Unable to load shipment rates. Please retry.");
        }
        finally {
            setLoading(false);
        }
    }, [page]);
    useEffect(() => { fetchSettings(); }, [fetchSettings]);
    const handleInputChange = (field: keyof FormData, value: string | number): void => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = {
                fromCountry: formData.fromCountry,
                toCountry: formData.toCountry,
                pricePerKg: Number(formData.pricePerKg),
                clearanceFee: Number(formData.clearanceFee),
                deliveryDays: formData.deliveryDays,
                currency: formData.currency,
                category: formData.category,
                goodType: formData.goodType,
                shipmentType: formData.shipmentType,
                deliveryType: formData.deliveryType,
                weight: Number(formData.weight),
            };
            if (editMode && editingItem) {
                await updateShipmentRate(editingItem.id, payload);
                toast.success("Shipment rate updated successfully!", {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
            else {
                await createShipmentSettings(payload);
                toast.success("New shipment rate created successfully!", {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
            resetForm();
            await fetchSettings();
        }
        catch (error: any) {
            if (error.response?.data) {
                const backendError = error.response.data;
                if (backendError.field && backendError.message) {
                    toast.error(`${backendError.field}: ${backendError.message}`, {
                        position: "top-right",
                        autoClose: 5000,
                    });
                }
                else if (backendError.message) {
                    toast.error(backendError.message, {
                        position: "top-right",
                        autoClose: 5000,
                    });
                }
                else if (Array.isArray(backendError.errors)) {
                    backendError.errors.forEach((err: any) => {
                        toast.error(`${err.field}: ${err.message}`, {
                            position: "top-right",
                            autoClose: 5000,
                        });
                    });
                }
            }
            else if (error instanceof Error) {
                toast.error(error.message, {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
            else {
                toast.error("An error occurred while saving the shipment rate", {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const resetForm = () => {
        setFormData({
            fromCountry: "",
            toCountry: "",
            pricePerKg: 0,
            clearanceFee: 0,
            deliveryDays: "",
            currency: "NGN",
            category: "",
            goodType: "",
            shipmentType: "",
            deliveryType: "",
            weight: 0,
        });
        setEditMode(false);
        setEditingItem(null);
        setViewMode("table");
    };
    const handleEdit = (setting: ShipmentSetting): void => {
        const mappedSetting: ShipmentSettingState = {
            id: setting.id,
            fromCountry: setting.fromCountry,
            toCountry: setting.toCountry,
            pricePerKg: setting.pricePerKg,
            clearanceFee: setting.clearanceFee,
            deliveryDays: setting.deliveryDays,
            currency: setting.currency,
            category: setting.category || "",
            goodType: setting.goodType || "",
            shipmentType: setting.shipmentType || "",
            deliveryType: setting.deliveryType || "",
            weight: setting.weight || "",
            createdAt: setting.createdAt,
            updatedAt: setting.updatedAt,
        };
        setEditingItem(mappedSetting);
        setFormData({
            fromCountry: setting.fromCountry,
            toCountry: setting.toCountry,
            pricePerKg: setting.pricePerKg,
            clearanceFee: setting.clearanceFee,
            deliveryDays: setting.deliveryDays,
            currency: setting.currency,
            category: setting.category || "",
            goodType: setting.goodType || "",
            shipmentType: setting.shipmentType || "",
            deliveryType: setting.deliveryType || "",
            weight: setting.weight || 0,
        });
        setEditMode(true);
        setViewMode("form");
    };
    const handleBack = (): void => {
        setViewMode("table");
        setEditMode(false);
        setEditingItem(null);
        setFormData({
            fromCountry: "",
            toCountry: "",
            pricePerKg: 0,
            clearanceFee: 0,
            deliveryDays: "0",
            currency: "NGN",
            category: "",
            goodType: "",
            shipmentType: "",
            deliveryType: "",
            weight: 1,
        });
    };
    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString();
    };
    if (error)
        return <p role="alert" className="integration-error">{error} <Button onClick={fetchSettings}>Retry</Button></p>;
    if (loading)
        return <div className="page-loading">Loading Settings...</div>;
    return (<div>
      {viewMode === "table" ? (<div>
          <div className="card">
            <div className="card-header">
              <div className="search-container">
                {settings.length > 0 && (<Can permission="shipping_rates.manage"><button className="button button-primary" onClick={() => setViewMode("form")}>
                    + Create Setting
                  </button></Can>)}
              </div>
            </div>
            <div className="card-content">
              {settings.length > 0 ? (<div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>From Country</th>
                        <th>To Country</th>
                        <th>Price Per Kg</th>
                        <th>Clearance Fee</th>
                        <th>Delivery Days</th>
                        <th>Currency</th>
                        <th>Date Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settings
                    .filter((setting) => matchesSearch(setting.fromCountry || "", searchTerm) ||
                    matchesSearch(setting.toCountry || "", searchTerm) ||
                    matchesSearch(setting.category || "", searchTerm) ||
                    matchesSearch(setting.goodType || "", searchTerm))
                    .map((setting) => (<tr key={setting.id}>
                          <td data-label="From">
                            <span className="badge badge-secondary">
                              {setting.fromCountry}
                            </span>
                          </td>
                          <td data-label="To">
                            <span className="badge badge-outline">
                              {setting.toCountry}
                            </span>
                          </td>
                          <td data-label="Price/kg" className="currency">
                            {formatMoney(setting.pricePerKg, setting.currency)}
                          </td>
                          <td data-label="Clearance" className="currency">
                            {formatMoney(setting.clearanceFee, setting.currency)}
                          </td>
                          <td data-label="Delivery Days">{setting.deliveryDays}</td>
                          <td data-label="Currency">{setting.currency}</td>
                          <td data-label="Created">{formatDate(setting.createdAt)}</td>
                          <td data-label="Actions">
                            <div className="table-action-buttons">
                              <Can permission="shipping_rates.manage"><button type="button" className="table-action-btn" title="Edit" aria-label="Edit rate" onClick={() => handleEdit(setting as any)}>
                                <IoPencil />
                              </button></Can>
                              <ComingSoonAction title="Delete rate — coming soon">
                                <button type="button" className="table-action-btn danger" title="Delete" aria-label="Delete rate" disabled>
                                  <IoTrashOutline />
                                </button>
                              </ComingSoonAction>
                            </div>
                          </td>
                        </tr>))}
                    </tbody>
                  </table>
                </div>) : (<div className="empty-state">
                  <div className="empty-content">
                    <img src={noShipmentIcon} alt="No shipment settings"/>
                    <h3>No shipment settings found</h3>
                    <p>Create your first shipment setting to get started</p>
                    <Can permission="shipping_rates.manage"><button className="button button-primary" onClick={() => setViewMode("form")}>
                      + Create First Setting
                    </button></Can>
                  </div>
                </div>)}
            </div>
          </div>
          <p className="integration-hint">Search applies to the current page of shipping rates.</p>
          <Pagination currentPage={page} totalPages={pages} totalItems={total} onPageChange={setPage} loading={loading} />
        </div>) : (<div className="card">
          <div className="card-header">
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <button className="button button-secondary" onClick={handleBack}>
                ← Back
              </button>
              <h2 className="card-title">
                {editMode
                ? "Edit Shipment Setting"
                : "Create New Shipment Setting"}
              </h2>
            </div>
          </div>
          <div className="card-content">
            <form onSubmit={handleSubmit}>
              <div className="form-grid-custom">
                <div className="form-field-container">
                  <div className="form-field-header">
                    <label className="form-label">From Country</label>
                  </div>
                  <Select className="form-input" aria-label="From Country" value={formData.fromCountry} onChange={(e) => handleInputChange("fromCountry", e.target.value)} required>
                    <option value="" disabled>Select origin country</option>
                    <option value="UK">UK</option>
                    <option value="Nigeria">Nigeria</option>
                  </Select>
                </div>

                <div className="form-field-container">
                  <div className="form-field-header">
                    <label className="form-label">To Country</label>
                  </div>
                  <Select className="form-input" aria-label="To Country" value={formData.toCountry} onChange={(e) => handleInputChange("toCountry", e.target.value)} required>
                    <option value="" disabled>Select destination country</option>
                    <option value="UK">UK</option>
                    <option value="Nigeria">Nigeria</option>
                  </Select>
                </div>

                <div className="form-field-container"><label className="form-label" htmlFor="rate-currency">Currency</label><Select id="rate-currency" className="form-input" value={formData.currency} onChange={event => handleInputChange("currency", event.target.value)}>{!["NGN", "GBP", "USD", "EUR"].includes(formData.currency) && <option value={formData.currency}>{formData.currency}</option>}{["NGN", "GBP", "USD", "EUR"].map(currency => <option key={currency} value={currency}>{currency}</option>)}</Select></div>
                <div className="form-field-container">
                  <div className="form-field-header">
                    <label className="form-label">Price Per Kg</label>
                  </div>
                  <input type="number" className="form-input" value={formData.pricePerKg} onChange={(e) => handleInputChange("pricePerKg", e.target.value)} placeholder="Enter price per kg" min="0" step="0.01"/>
                </div>

                <div className="form-field-container">
                  <div className="form-field-header">
                    <label className="form-label">Clearance Fee</label>
                  </div>
                  <input type="number" className="form-input" value={formData.clearanceFee} onChange={(e) => handleInputChange("clearanceFee", e.target.value)} placeholder="Enter clearance fee" min="0" step="0.01"/>
                </div>

                <div className="form-field-container">
                  <div className="form-field-header">
                    <label className="form-label">Delivery Days</label>
                  </div>
                  <input type="text" className="form-input" value={formData.deliveryDays} onChange={(e) => handleInputChange("deliveryDays", e.target.value)} placeholder="Enter delivery days" min="1"/>
                </div>

                <div className="form-field-container">
                  <div className="form-field-header">
                    <label className="form-label">Category</label>
                  </div>
                  <input type="text" className="form-input" value={formData.category} onChange={(e) => handleInputChange("category", e.target.value)} placeholder="Enter category"/>
                </div>

                <div className="form-field-container">
                  <div className="form-field-header">
                    <label className="form-label">Good Type</label>
                  </div>
                  <input type="text" className="form-input" value={formData.goodType} onChange={(e) => handleInputChange("goodType", e.target.value)} placeholder="Enter good type"/>
                </div>

                <div className="form-field-container">
                  <div className="form-field-header">
                    <label className="form-label">Shipment Type</label>
                  </div>
                  <input type="text" className="form-input" value={formData.shipmentType} onChange={(e) => handleInputChange("shipmentType", e.target.value)} placeholder="Enter shipment type"/>
                </div>

                <div className="form-field-container">
                  <div className="form-field-header">
                    <label className="form-label">Weight (kg)</label>
                  </div>
                  <input type="number" className="form-input" value={formData.weight} onChange={(e) => handleInputChange("weight", e.target.value)} placeholder="Enter weight in kg" min="1" step="0.1"/>
                </div>
              </div>

              <div className="form-field-container">
                <div className="form-field-header">
                  <label className="form-label">Delivery Type</label>
                </div>
                <input type="text" className="form-input" value={formData.deliveryType} onChange={(e) => handleInputChange("deliveryType", e.target.value)} placeholder="Enter delivery type"/>
              </div>

              <div style={{ marginTop: "24px", display: "flex", gap: "12px" }}>
                <Can permission="shipping_rates.manage"><button type="submit" className="button button-primary" disabled={isSubmitting}>
                  {isSubmitting ? (<span className="button-content">
                      <span className="spinner"></span>
                      {editMode ? "Updating..." : "Creating..."}
                    </span>) : editMode ? ("Update Setting") : ("Create Setting")}
                </button></Can>
                <button type="button" className="button button-secondary" onClick={handleBack} disabled={isSubmitting}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>)}
    </div>);
};
export default ShipmentPage;
